// Another save of the project, as this tab takes it in (spec docs/specs/editor/live-sync-merge.md):
// a logged change (`EditChange`, spec live-sync.md) applied to the timeline this tab last saved or
// took in, which gives the timeline the change's version holds. The reducer's `sync` then merges
// that into the draft.
import type { EditChange } from "../../api/generated/model";
import { liveFrame } from "./engine/project";
import type { Timeline } from "./timeline";
import { fromWireCaptions, fromWireClip, fromWireMarker, fromWireTrack, fromWireTransition } from "./wire";

/** Items by id: each of `put` replacing its own or added after them, each of `removed` gone. */
function byId<T extends { id: string }>(items: readonly T[], put: readonly T[], removed: readonly string[]): readonly T[] {
  if (put.length === 0 && removed.length === 0) return items;
  const gone = new Set(removed);
  const replaced = new Map(put.map((item) => [item.id, item]));
  const known = new Set(items.map((item) => item.id));
  return [
    ...items.filter((item) => !gone.has(item.id)).map((item) => replaced.get(item.id) ?? item),
    ...put.filter((item) => !known.has(item.id)),
  ];
}

/**
 * The timeline a change's version holds, from the one before it: clips, transitions and markers
 * put or removed by id, the tracks, the captions' look and the frame replaced when the change has
 * them. Its clips are read at the frame the change was saved in. An empty change gives `base`.
 */
export function applyChange(base: Timeline, change: EditChange): Timeline {
  const aspect = change.aspect ?? base.aspect;
  const frame = liveFrame(aspect);
  const markers = byId(base.markers, change.markers.map(fromWireMarker), change.removed_markers);
  const next: Timeline = {
    tracks: change.tracks === null ? base.tracks : change.tracks.map(fromWireTrack),
    clips: byId(base.clips, change.clips.map((clip) => fromWireClip(clip, frame)), change.removed_clips),
    transitions: byId(base.transitions, change.transitions.map(fromWireTransition), change.removed_transitions),
    captions: change.captions === null ? base.captions : fromWireCaptions(change.captions.look),
    aspect,
    markers: markers === base.markers ? markers : [...markers].sort((one, other) => one.atMs - other.atMs),
  };
  const parts = Object.keys(next) as (keyof Timeline)[];
  return parts.every((part) => next[part] === base[part]) ? base : next;
}
